<template>
  <PublicPageShell>
    <template #header>
      <nav
        aria-label="Primary navigation"
        class="app-content-container app-content-container--wide relative flex items-center justify-between border-b border-white/10 py-3 sm:py-4"
      >
        <RouterLink
          to="/"
          data-home-brand
          aria-label="PermitPilott home"
          class="flex min-w-0 items-center gap-2 md:gap-2.5"
        >
          <AppLogo :size="36" class="h-8 w-8 shrink-0 md:h-9 md:w-9" />
          <span
            data-home-wordmark
            class="text-sm font-extrabold tracking-tight max-[24rem]:hidden min-[360px]:text-base sm:text-lg md:text-xl"
          >
            PERMIT<span class="text-[var(--brand-accent)]">PILOTT</span>
          </span>
        </RouterLink>

        <div class="hidden items-center gap-2 sm:flex md:gap-4">
          <a
            href="#workflow"
            class="hidden min-h-11 items-center px-3 text-sm font-medium text-[var(--color-text-muted)] transition-colors hover:text-white md:inline-flex"
          >
            How it works
          </a>
          <RouterLink
            to="/pricing"
            class="hidden min-h-11 items-center px-3 text-sm font-medium text-[var(--color-text-muted)] transition-colors hover:text-white sm:inline-flex"
          >
            Pricing
          </RouterLink>
          <RouterLink
            to="/login"
            class="inline-flex min-h-11 items-center whitespace-nowrap px-1.5 text-sm font-medium text-[var(--color-text-muted)] transition-colors hover:text-white sm:px-3"
          >
            Sign in
          </RouterLink>
          <RouterLink
            to="/signup"
            class="inline-flex min-h-11 items-center whitespace-nowrap rounded-lg bg-[var(--accent-primary)] px-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--accent-primary-hover)] sm:px-5"
          >
            Start Free
          </RouterLink>
        </div>

        <UiMenu
          v-model="mobileMenuOpen"
          :trigger-label="
            mobileMenuOpen
              ? 'Close primary navigation'
              : 'Open primary navigation'
          "
          panel-label="Primary navigation"
          class="relative shrink-0 sm:hidden"
          trigger-class="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-white/15 px-3 text-sm font-semibold text-white transition-colors hover:border-white/30 hover:bg-white/5"
          panel-class="absolute right-0 top-[calc(100%+0.5rem)] z-30 w-56 overflow-hidden rounded-xl border border-white/10 bg-[var(--bg-secondary)] p-2 shadow-2xl shadow-black/40"
          data-home-mobile-menu
        >
          <template #trigger="{ open }">
            <span>{{ open ? "Close" : "Menu" }}</span>
            <UiIcon :name="open ? 'x' : 'arrow-down'" :size="17" />
          </template>
          <template #panel="{ close }">
            <a
              href="#workflow"
              role="menuitem"
              class="flex min-h-11 items-center rounded-lg px-3 text-sm font-medium text-[var(--color-text-muted)] hover:bg-white/5 hover:text-white"
              @click="close()"
            >
              How it works
            </a>
            <RouterLink
              to="/pricing"
              role="menuitem"
              class="flex min-h-11 items-center rounded-lg px-3 text-sm font-medium text-[var(--color-text-muted)] hover:bg-white/5 hover:text-white"
              @click="close()"
            >
              Pricing
            </RouterLink>
            <RouterLink
              to="/login"
              role="menuitem"
              class="flex min-h-11 items-center rounded-lg px-3 text-sm font-medium text-[var(--color-text-muted)] hover:bg-white/5 hover:text-white"
              @click="close()"
            >
              Sign in
            </RouterLink>
            <RouterLink
              to="/signup"
              role="menuitem"
              class="mt-1 flex min-h-11 items-center justify-center rounded-lg bg-[var(--accent-primary)] px-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--accent-primary-hover)]"
              @click="close()"
            >
              Start Free
            </RouterLink>
          </template>
        </UiMenu>
      </nav>
    </template>

    <section
      data-home-hero
      class="app-content-container app-content-container--wide grid gap-10 pb-14 pt-10 sm:pb-16 sm:pt-14 lg:grid-cols-2 lg:items-center lg:gap-8 lg:pb-24 lg:pt-20 xl:grid-cols-[0.86fr_1.14fr] xl:gap-14"
    >
      <div class="max-w-2xl">
        <h1
          class="max-w-xl text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-[3.5rem]"
        >
          From permit PDF to a road-ready route.
        </h1>
        <p
          class="mt-6 max-w-xl text-base leading-7 text-[var(--color-text-muted)] sm:text-lg sm:leading-8"
        >
          Upload a permit, review the route, then navigate in-app or export GPX.
        </p>

        <div class="mt-8 flex flex-col gap-3 sm:flex-row">
          <RouterLink
            to="/signup"
            data-home-hero-primary
            class="inline-flex min-h-12 items-center justify-center rounded-lg bg-[var(--accent-primary)] px-4 text-center text-base font-semibold text-white shadow-lg shadow-blue-950/40 transition-colors hover:bg-[var(--accent-primary-hover)] sm:px-7"
          >
            Start Free (3 Routes)
          </RouterLink>
          <a
            href="#workflow"
            data-home-hero-secondary
            class="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-white/20 px-4 text-center text-base font-semibold text-white transition-colors hover:border-white/40 sm:px-7"
          >
            See the Workflow
            <UiIcon name="arrow-down" :size="18" />
          </a>
        </div>

        <p
          data-signup-assurance
          class="mt-4 flex items-center gap-2 text-sm text-[var(--color-text-muted)]"
        >
          <UiIcon
            name="circle-check"
            :size="17"
            class="text-[var(--color-success)]"
          />
          No credit card required.
        </p>
      </div>

      <div
        data-product-preview
        role="img"
        aria-label="PermitPilott preview showing a permit becoming a reviewed route that is ready to navigate"
        class="pointer-events-none select-none overflow-hidden rounded-2xl border border-white/10 bg-[var(--bg-secondary)] shadow-2xl shadow-black/20"
      >
        <div aria-hidden="true">
          <div
            class="grid grid-cols-3 border-b border-white/10 px-4 py-4 sm:px-6"
          >
            <div
              v-for="(stage, index) in heroStages"
              :key="stage.label"
              class="flex min-w-0 items-center gap-2 text-xs font-semibold sm:text-sm"
            >
              <span
                class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                :class="
                  index === heroStages.length - 1
                    ? 'bg-[var(--color-success)]/20 text-[var(--color-success)]'
                    : 'bg-[var(--accent-primary)] text-white'
                "
              >
                <UiIcon
                  v-if="index === heroStages.length - 1"
                  name="check"
                  :size="15"
                />
                <span v-else>{{ index + 1 }}</span>
              </span>
              <span
                class="text-white/90 max-[24rem]:hidden sm:hidden lg:inline xl:hidden"
                >{{ stage.shortLabel }}</span
              >
              <span
                class="hidden text-white/90 sm:inline lg:hidden xl:inline"
                >{{ stage.label }}</span
              >
            </div>
          </div>

          <div
            class="grid min-w-0 gap-4 p-4 sm:p-6 md:grid-cols-[0.62fr_1.38fr]"
          >
            <div
              class="min-w-0 rounded-xl border border-white/10 bg-[#0b1629] p-4"
            >
              <div
                class="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]"
              >
                Upload Permit
              </div>

              <div
                class="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-[var(--color-success)]/30 bg-[var(--color-success)]/5 p-3"
              >
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--accent-primary)] text-xs font-bold"
                  >1</span
                >
                <div class="min-w-0 flex-1">
                  <div class="truncate text-xs font-medium">TX-permit.pdf</div>
                  <div class="text-xs text-[var(--color-text-muted)]">
                    24 lines
                  </div>
                </div>
                <UiIcon
                  name="circle-check"
                  :size="16"
                  class="text-[var(--color-success)]"
                />
              </div>

              <div
                class="mt-4 flex min-h-10 items-center justify-center gap-2 rounded-lg bg-[var(--accent-primary)] px-3 text-center text-xs font-bold"
              >
                <UiIcon name="truck" :size="16" />
                Generate Route
              </div>
            </div>

            <div
              class="min-w-0 overflow-hidden rounded-xl border border-white/10"
            >
              <div
                class="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 bg-[#0b1629] px-4 py-3"
              >
                <span class="whitespace-nowrap text-sm font-semibold">
                  <span class="lg:hidden xl:inline">Route Map</span>
                  <span class="hidden lg:inline xl:hidden">Route</span>
                </span>
                <span
                  class="flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-[var(--color-success)]"
                >
                  <UiIcon name="circle-check" :size="16" />
                  Ready
                </span>
              </div>
              <div class="relative min-h-64 overflow-hidden bg-[#0a1a31]">
                <svg
                  data-home-preview-map
                  class="absolute inset-0 h-full w-full overflow-hidden"
                  viewBox="0 0 520 300"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M-30 55C76 84 114 13 206 54C277 86 307 38 382 56C446 72 476 37 552 18"
                    stroke="#263852"
                    stroke-width="3"
                  />
                  <path
                    d="M-24 242C80 190 132 238 206 196C289 149 335 190 404 142C452 109 490 114 546 92"
                    stroke="#263852"
                    stroke-width="4"
                  />
                  <path
                    d="M86 -18C97 49 67 109 111 159C146 200 134 242 151 322"
                    stroke="#22334d"
                    stroke-width="2"
                  />
                  <path
                    d="M403 -18C386 47 422 86 399 147C379 201 421 238 390 322"
                    stroke="#22334d"
                    stroke-width="2"
                  />
                  <path
                    d="M36 247C105 225 119 151 186 168C247 184 263 119 323 130C385 141 402 73 480 53"
                    stroke="#3b82f6"
                    stroke-width="7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <circle
                    cx="36"
                    cy="247"
                    r="11"
                    fill="#22c55e"
                    stroke="#f8fafc"
                    stroke-width="4"
                  />
                  <circle
                    cx="186"
                    cy="168"
                    r="9"
                    fill="#3b82f6"
                    stroke="#f8fafc"
                    stroke-width="4"
                  />
                  <circle
                    cx="323"
                    cy="130"
                    r="9"
                    fill="#3b82f6"
                    stroke="#f8fafc"
                    stroke-width="4"
                  />
                  <circle
                    cx="480"
                    cy="53"
                    r="11"
                    fill="#ef4444"
                    stroke="#f8fafc"
                    stroke-width="4"
                  />
                </svg>
                <div
                  class="absolute left-3 top-3 flex flex-col gap-1 rounded-lg border border-gray-200 bg-white p-1.5 text-xs font-semibold text-gray-600 shadow-lg"
                >
                  <span class="flex items-center gap-1 rounded px-2 py-1"
                    ><UiIcon name="map" :size="13" /> Layers</span
                  >
                  <span class="flex items-center gap-1 rounded px-2 py-1"
                    ><UiIcon name="construction" :size="13" /> Structures</span
                  >
                </div>
                <div
                  class="absolute right-3 top-3 flex flex-col overflow-hidden rounded-lg border border-white/10 bg-[#07111f]"
                >
                  <span class="flex h-10 w-10 items-center justify-center">
                    <UiIcon name="plus" :size="18" />
                  </span>
                  <span
                    class="flex h-10 w-10 items-center justify-center border-t border-white/10 text-xl"
                    >−</span
                  >
                </div>
              </div>
              <div class="bg-[#0b1629] px-4 py-3">
                <div class="mb-2 text-xs text-[var(--color-text-muted)]">
                  Drag markers to adjust
                </div>
                <div
                  data-home-preview-actions
                  class="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2"
                >
                  <span
                    class="inline-flex min-w-0 min-h-9 items-center justify-center gap-1.5 rounded-md border border-white/15 px-3 text-center text-xs font-semibold"
                  >
                    <UiIcon name="download" :size="15" />
                    Download GPX
                  </span>
                  <span
                    class="inline-flex min-w-0 min-h-9 items-center justify-center gap-1.5 rounded-md bg-[var(--accent-primary)] px-3 text-center text-xs font-semibold"
                  >
                    <UiIcon name="navigation" :size="15" />
                    Navigate In-App
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          aria-hidden="true"
          class="border-t border-white/10 px-4 py-2 text-center text-xs text-[var(--color-text-muted)]"
        >
          Product preview
        </div>
      </div>
    </section>

    <section
      id="workflow"
      class="scroll-mt-4 border-y border-white/5 bg-white/[0.02]"
    >
      <div
        class="app-content-container app-content-container--wide py-14 sm:py-16 md:py-20"
      >
        <div class="max-w-2xl">
          <h2 class="text-3xl font-extrabold tracking-tight sm:text-4xl">
            From permit to route in four steps.
          </h2>
          <p class="mt-4 text-base text-[var(--color-text-muted)] sm:text-lg">
            Upload, verify, and drive with the route in the format you need.
          </p>
        </div>

        <ol
          class="ml-5 mt-10 grid gap-0 border-l border-white/15 sm:mt-12 md:ml-0 md:grid-cols-4 md:border-l-0 md:border-t"
        >
          <li
            v-for="(step, index) in workflowSteps"
            :key="step.title"
            data-home-workflow-step
            class="relative grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-x-4 px-5 py-6 md:block md:px-0 md:pb-0 md:pr-8 md:pt-8"
          >
            <span
              class="-ml-[2.35rem] flex h-10 w-10 items-center justify-center rounded-full border-2 border-[var(--accent-primary)] bg-[var(--bg-primary)] text-sm font-bold text-white md:absolute md:-top-5 md:left-0 md:ml-0"
            >
              {{ index + 1 }}
            </span>
            <div class="min-w-0 break-words">
              <div
                class="flex items-center gap-2 text-[var(--accent-primary-text)]"
              >
                <UiIcon :name="step.icon" :size="21" />
                <h3
                  class="min-w-0 break-words text-base font-bold text-white sm:text-lg"
                >
                  {{ step.title }}
                </h3>
              </div>
              <p
                class="mt-2 break-words text-sm leading-6 text-[var(--color-text-muted)]"
              >
                {{ step.description }}
              </p>
            </div>
          </li>
        </ol>

        <div
          class="mt-10 grid border-y border-white/10 sm:mt-12 sm:grid-cols-3 sm:divide-x sm:divide-white/10"
        >
          <div
            v-for="outcome in workflowOutcomes"
            :key="outcome.label"
            class="flex items-center gap-3 border-b border-white/10 py-5 last:border-b-0 sm:justify-center sm:border-b-0 sm:px-6"
          >
            <UiIcon
              :name="outcome.icon"
              :size="22"
              class="text-[var(--color-success)]"
            />
            <span class="text-sm font-semibold">{{ outcome.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <section
      class="app-content-container app-content-container--wide py-16 sm:py-20 md:py-28"
    >
      <div
        class="grid items-center gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:gap-16"
      >
        <div
          data-product-preview
          role="img"
          aria-label="PermitPilott route map with editable waypoints, structure search, and a voice announcement editor"
          class="pointer-events-none select-none overflow-hidden rounded-2xl border border-white/10 bg-[var(--bg-secondary)] shadow-2xl shadow-black/20"
        >
          <div aria-hidden="true">
            <div
              class="flex items-center justify-between border-b border-white/10 px-4 py-3"
            >
              <div class="flex items-center gap-2 text-sm font-semibold">
                <UiIcon name="map" :size="18" />
                Route Map
              </div>
              <span class="text-xs text-[var(--color-text-muted)]">
                Drag markers to adjust
              </span>
            </div>

            <div class="relative min-h-[28rem] overflow-hidden bg-[#0a1a31]">
              <svg
                data-home-preview-map
                class="absolute inset-0 h-full w-full overflow-hidden"
                viewBox="0 0 640 460"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 90C80 131 170 51 287 91C403 131 458 47 636 83"
                  stroke="#263852"
                  stroke-width="4"
                />
                <path
                  d="M4 350C67 300 152 334 240 277C325 222 402 281 500 204C557 159 603 163 636 134"
                  stroke="#263852"
                  stroke-width="4"
                />
                <path
                  d="M82 -30C122 83 69 174 143 245C190 291 170 372 196 490"
                  stroke="#22334d"
                  stroke-width="3"
                />
                <path
                  d="M480 -30C445 79 505 153 444 237C399 300 450 370 412 490"
                  stroke="#22334d"
                  stroke-width="3"
                />
                <path
                  d="M41 365C111 336 123 267 211 280C294 292 303 197 382 209C461 221 481 126 587 87"
                  stroke="#3b82f6"
                  stroke-width="8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <circle
                  cx="41"
                  cy="365"
                  r="11"
                  fill="#22c55e"
                  stroke="#f8fafc"
                  stroke-width="4"
                />
                <circle
                  cx="211"
                  cy="280"
                  r="9"
                  fill="#3b82f6"
                  stroke="#f8fafc"
                  stroke-width="4"
                />
                <circle
                  cx="382"
                  cy="209"
                  r="9"
                  fill="#3b82f6"
                  stroke="#f8fafc"
                  stroke-width="4"
                />
                <circle
                  cx="587"
                  cy="87"
                  r="11"
                  fill="#ef4444"
                  stroke="#f8fafc"
                  stroke-width="4"
                />
              </svg>

              <div
                class="absolute left-3 top-3 flex flex-col gap-1 rounded-xl border border-gray-200 bg-white p-1.5 text-xs font-medium text-gray-600 shadow-lg sm:left-4 sm:top-4"
              >
                <span class="flex items-center gap-1.5 rounded-lg px-2.5 py-2"
                  ><UiIcon name="map" :size="17" /> Layers</span
                >
                <span class="flex items-center gap-1.5 rounded-lg px-2.5 py-2"
                  ><UiIcon name="construction" :size="17" /> Structures</span
                >
              </div>

              <div
                data-home-annotation-preview
                class="absolute bottom-4 right-4 w-64 max-w-[calc(100%-2rem)] overflow-hidden rounded-xl border border-[#334155] bg-[#1e293b] text-xs shadow-2xl"
              >
                <div class="border-b border-[#334155] p-3">
                  <div class="font-bold text-white">Exit 315</div>
                  <div class="mt-1 break-words text-xs text-[#94a3b8]">
                    Turn anchor · 34.76120, -84.93210
                  </div>
                </div>
                <div class="p-3">
                  <div
                    class="break-all text-xs uppercase tracking-wider text-[#94a3b8]"
                  >
                    Voice Announcement
                  </div>
                  <div
                    class="mt-1.5 break-words rounded-lg border border-[#334155] bg-[#0f172a] px-2.5 py-2 text-white"
                  >
                    Move right after Exit 315.
                  </div>
                  <div class="mt-1 text-xs text-[var(--color-text-muted)]">
                    Spoken aloud during navigation
                  </div>
                  <div
                    data-home-annotation-actions
                    class="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2"
                  >
                    <span
                      class="flex-1 rounded-lg bg-[#334155] px-3 py-1.5 text-center text-[#94a3b8]"
                      >Cancel</span
                    >
                    <span
                      class="flex-1 rounded-lg bg-[var(--accent-primary)] px-3 py-1.5 text-center font-medium text-white"
                      >Save</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            aria-hidden="true"
            class="border-t border-white/10 px-4 py-2 text-center text-xs text-[var(--color-text-muted)]"
          >
            Product preview
          </div>
        </div>

        <div>
          <h2 class="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Review the route before the wheels turn.
          </h2>
          <p
            class="mt-5 text-base leading-7 text-[var(--color-text-muted)] sm:text-lg"
          >
            Verify the route, correct waypoints, and add the guidance your
            driver needs before departure.
          </p>

          <div class="mt-8 divide-y divide-white/10 border-y border-white/10">
            <div
              v-for="proof in productProof"
              :key="proof.title"
              data-home-proof
              class="flex gap-4 py-5"
            >
              <UiIcon
                :name="proof.icon"
                :size="23"
                class="mt-0.5 shrink-0"
                :class="proof.color"
              />
              <div>
                <h3 class="font-bold">{{ proof.title }}</h3>
                <p
                  class="mt-1 text-sm leading-6 text-[var(--color-text-muted)]"
                >
                  {{ proof.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-14 border-y border-white/10 py-7 sm:mt-20 md:mt-24">
        <div class="grid gap-6 lg:grid-cols-[1fr_2.4fr] lg:items-center">
          <h2 class="text-2xl font-extrabold leading-tight">
            Built for the tools already in your cab.
          </h2>
          <div class="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-4">
            <div
              v-for="target in exportTargets"
              :key="target.label"
              data-home-export
              class="flex min-w-0 items-center gap-3 bg-[var(--bg-primary)] px-4 py-4 text-sm font-semibold"
            >
              <UiIcon
                :name="target.icon"
                :size="22"
                class="shrink-0 text-[var(--accent-primary-text)]"
              />
              <span class="min-w-0 break-words">{{ target.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="border-t border-white/10 bg-[#081426]">
      <div
        class="app-content-container app-content-container--wide grid items-center gap-10 py-14 sm:py-16 md:grid-cols-[0.9fr_1.1fr] md:py-20"
      >
        <div>
          <h2 class="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Turn your next permit into a route.
          </h2>
          <p class="mt-4 text-base text-[var(--color-text-muted)] sm:text-lg">
            Start with 3 free routes. No credit card required.
          </p>
          <div class="mt-8 flex flex-col gap-3 sm:flex-row">
            <RouterLink
              to="/signup"
              data-home-bottom-primary
              class="inline-flex min-h-12 items-center justify-center rounded-lg bg-[var(--accent-primary)] px-4 text-center text-base font-semibold text-white transition-colors hover:bg-[var(--accent-primary-hover)] sm:px-7"
            >
              Start Free (3 Routes)
            </RouterLink>
            <RouterLink
              to="/pricing"
              class="inline-flex min-h-12 items-center justify-center rounded-lg border border-white/20 px-7 text-base font-semibold text-white transition-colors hover:border-white/40"
            >
              View Pricing
            </RouterLink>
          </div>
        </div>

        <div class="relative hidden h-48 md:block" aria-hidden="true">
          <svg
            class="h-full w-full"
            viewBox="0 0 620 190"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 145C113 145 133 94 213 119C286 142 303 45 390 67C468 87 498 26 593 30"
              stroke="#ff6b00"
              stroke-width="5"
              stroke-linecap="round"
              stroke-dasharray="1 1"
            />
            <circle
              cx="12"
              cy="145"
              r="10"
              fill="#081426"
              stroke="#ff6b00"
              stroke-width="5"
            />
            <path
              d="M593 16C604 16 612 24 612 35C612 51 593 68 593 68C593 68 574 51 574 35C574 24 582 16 593 16Z"
              fill="#ff6b00"
            />
            <circle cx="593" cy="35" r="6" fill="#081426" />
          </svg>
        </div>
      </div>
    </section>
  </PublicPageShell>
</template>

<script setup>
import { ref } from "vue";
import { RouterLink } from "vue-router";
import AppLogo from "../components/AppLogo.vue";
import PublicPageShell from "../components/PublicPageShell.vue";
import UiIcon from "../components/ui/UiIcon.vue";
import UiMenu from "../components/ui/UiMenu.vue";

const mobileMenuOpen = ref(false);

const heroStages = [
  { label: "Upload permit", shortLabel: "Upload" },
  { label: "Review route", shortLabel: "Review" },
  { label: "Ready to navigate", shortLabel: "Ready" },
];

const workflowSteps = [
  {
    icon: "upload",
    title: "Upload your permit",
    description: "Add your permit as a PDF or photo.",
  },
  {
    icon: "scan",
    title: "Generate the route",
    description:
      "PermitPilott orders the roads and turns listed on the permit.",
  },
  {
    icon: "waypoints",
    title: "Review and adjust",
    description: "Check the route and correct waypoints before departure.",
  },
  {
    icon: "navigation",
    title: "Navigate or export",
    description: "Navigate in-app or download GPX for another device.",
  },
];

const workflowOutcomes = [
  { icon: "file", label: "All 50 states" },
  { icon: "route", label: "Editable route" },
  { icon: "flag", label: "In-app or GPX" },
];

const productProof = [
  {
    icon: "map",
    title: "Review against your permit",
    description: "Compare permitted roads and turns on the route map.",
    color: "text-[var(--accent-primary-text)]",
  },
  {
    icon: "waypoints",
    title: "Correct any waypoint",
    description: "Drag markers, add route points, and recalculate.",
    color: "text-[var(--accent-primary-text)]",
  },
  {
    icon: "construction",
    title: "Find bridge structures",
    description: "Search by state and structure number, then add a waypoint.",
    color: "text-[var(--color-warning)]",
  },
  {
    icon: "audio",
    title: "Add spoken guidance",
    description: "Attach a voice announcement to any waypoint.",
    color: "text-[var(--color-success)]",
  },
];

const exportTargets = [
  { icon: "file", label: "Garmin GPX" },
  { icon: "map-pinned", label: "Google Maps" },
  { icon: "apple", label: "Apple Maps" },
  { icon: "navigation", label: "In-app navigation" },
];
</script>
